import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiFilter, FiX, FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { getProducts, getCategories } from '../services/api';
import ProductCard from '../components/product/ProductCard';
import PageTransition from '../components/common/PageTransition';
import SkeletonCard from '../components/common/SkeletonCard';
import './ProductsPage.css';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'popular', label: 'Best Sellers' },
];

const DEMO_CATEGORIES = [
  { _id: 'c1', name: 'Electronics' },
  { _id: 'c2', name: 'Fashion' },
  { _id: 'c3', name: 'Home & Living' },
  { _id: 'c4', name: 'Sports & Fitness' },
  { _id: 'c5', name: 'Books' },
];

const LIMIT = 12;

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filters
  const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

  const debounceRef = useRef(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, page, limit: LIMIT };
      if (search) params.search = search;
      if (category) params.category = category;
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;

      const { data } = await getProducts(params);
      setProducts(data.products);
      setTotal(data.pagination.total);
    } catch {
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [sort, search, category, minPrice, maxPrice, page]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await getCategories();
      setCategories(data.categories);
    } catch {
      setCategories(DEMO_CATEGORIES);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Debounce search input
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const clearFilters = () => {
    setSort('newest');
    setCategory('');
    setMinPrice('');
    setMaxPrice('');
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const hasFilters = sort !== 'newest' || category || minPrice || maxPrice || search;
  const totalPages = Math.ceil(total / LIMIT);

  const Sidebar = () => (
    <aside className="products-sidebar">
      <div className="sidebar-header-row">
        <h3 className="sidebar-title">Filters</h3>
        {hasFilters && (
          <button className="clear-btn" onClick={clearFilters}>Clear all</button>
        )}
      </div>

      {/* Sort */}
      <div className="filter-group">
        <label className="filter-label">Sort by</label>
        <select
          className="form-select"
          value={sort}
          onChange={(e) => { setSort(e.target.value); setPage(1); }}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Price Range */}
      <div className="filter-group">
        <label className="filter-label">Price Range</label>
        <div className="price-inputs">
          <input
            className="form-input"
            type="number"
            placeholder="Min Rs."
            value={minPrice}
            min={0}
            onChange={(e) => { setMinPrice(e.target.value); setPage(1); }}
          />
          <span className="price-dash">–</span>
          <input
            className="form-input"
            type="number"
            placeholder="Max Rs."
            value={maxPrice}
            min={0}
            onChange={(e) => { setMaxPrice(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      {/* Categories */}
      <div className="filter-group">
        <label className="filter-label">Category</label>
        <div className="category-list">
          <label className="category-radio">
            <input
              type="radio"
              name="category"
              value=""
              checked={category === ''}
              onChange={() => { setCategory(''); setPage(1); }}
            />
            <span>All Categories</span>
          </label>
          {categories.map((cat) => (
            <label key={cat._id} className="category-radio">
              <input
                type="radio"
                name="category"
                value={cat._id}
                checked={category === cat._id}
                onChange={() => { setCategory(cat._id); setPage(1); }}
              />
              <span>{cat.name}</span>
            </label>
          ))}
        </div>
      </div>
    </aside>
  );

  return (
    <PageTransition>
    <div className="products-page">
      {/* Hero */}
      <div className="products-hero">
        <div className="container">
          <h1>Shop All Products</h1>
          <p>Discover thousands of items from trusted vendors</p>
        </div>
      </div>

      <div className="container products-layout">
        {/* Desktop sidebar */}
        <div className="sidebar-desktop">
          <Sidebar />
        </div>

        {/* Main */}
        <div className="products-main">
          {/* Toolbar */}
          <div className="products-toolbar">
            <div className="toolbar-left">
              <button
                className="btn btn-ghost btn-sm filter-toggle"
                onClick={() => setSidebarOpen(true)}
              >
                <FiFilter size={14} /> Filters
                {hasFilters && <span className="filter-dot" />}
              </button>
              {!loading && (
                <span className="results-count">
                  {total} result{total !== 1 ? 's' : ''}
                  {search && <> for "<strong>{search}</strong>"</>}
                </span>
              )}
            </div>
            <div className="toolbar-search">
              <FiSearch className="search-icon" size={14} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="toolbar-search-input"
              />
              {searchInput && (
                <button className="search-clear" onClick={() => { setSearchInput(''); setSearch(''); }}>
                  <FiX size={13} />
                </button>
              )}
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="products-grid">
              {Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🛍️</div>
              <h3>No products found</h3>
              <p>Try adjusting your filters or search term.</p>
              <button className="btn btn-primary" onClick={clearFilters}>Clear Filters</button>
            </div>
          ) : (
            <div className="products-grid">
              {products.map((p) => (
                <ProductCard key={p._id} product={p} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <FiChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && arr[idx - 1] !== p - 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '…' ? (
                    <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
                  ) : (
                    <button
                      key={p}
                      className={`page-btn ${p === page ? 'active' : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                className="page-btn"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <FiChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              className="sidebar-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              className="sidebar-drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
            >
              <div className="drawer-top">
                <span className="drawer-title">Filters</span>
                <button className="drawer-close" onClick={() => setSidebarOpen(false)}>
                  <FiX size={20} />
                </button>
              </div>
              <Sidebar />
              <div className="drawer-footer">
                <button
                  className="btn btn-primary btn-block"
                  onClick={() => setSidebarOpen(false)}
                >
                  Show {total} Results
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
    </PageTransition>
  );
}
